import {
  AgxAvatarLabelGroup,
  AgxBadge,
  AgxSpacer,
  AgxTable,
  AgxTypography,
  Breakpoints,
  IColumnConfig,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import { AgencyAgentModel } from '@urbanx/agx.agencies.client';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import placeHolderImage from 'assets/images/placeholder-property.png';
import { CampaignTableColumnNames, SearchableCampaign } from 'types/campaigns';
import { Pages } from 'constants/menu';
import { formatCampaignStageLabel } from 'utils/campaignUtils';
import { getStatusConfig } from './components/campaignMarketing/CampaignFlowStatusBadge';
import CampaignOptions from './components/options/CampaignOptions';
import { CampaignActionRequestCampaignAction } from '@urbanx/agx.campaigns.client';

interface CampaignsTableProps {
  agencyId: string;
  campaigns: SearchableCampaign[];
  agents: AgencyAgentModel[];
  isLoading: boolean;
  isArchivedTab: boolean;
  onPerformCampaignAction: (
    campaignId: string,
    campaignAction: CampaignActionRequestCampaignAction
  ) => void;
}

// 48px property image plus responsive cell padding and a 1px row border.
const CAMPAIGN_ROW_HEIGHT = 65;
const CAMPAIGN_DESKTOP_ROW_HEIGHT = 81;
const CAMPAIGN_ROW_OVERSCAN = 8;

const campaignsTableHeaders: IColumnConfig[] = [
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.Campaign,
  },
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.Status,
  },
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.LeadAgent,
  },
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.SecondAgent,
  },
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.Marketing,
  },
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.Contract,
  },
  {
    align: 'left',
    onClick: () => {},
    name: CampaignTableColumnNames.Launch,
  },
  {
    align: 'left',
    onClick: (event) => event.stopPropagation(),
    name: CampaignTableColumnNames.Action,
    disableRowLink: true,
  },
];

export function CampaignsTable({
  agencyId,
  campaigns,
  agents,
  isLoading,
  isArchivedTab,
  onPerformCampaignAction,
}: CampaignsTableProps) {
  const screenSize = useWindowSize();
  const campaignColumns = useMemo(() => {
    if (!campaigns) return [];

    return campaigns?.map((campaign, index) => {
      const [addressLineOne, addressLineTwo] = campaign.cleanAddress;
      const leadAgent = agents.find((agent) => agent.id === campaign.leadAgentId);
      const secondAgent = agents.find((agent) => agent.id === campaign.secondAgentId);
      const isLaunched = campaign.filterableLaunchDate?.isBefore(dayjs(), 'day');
      const launchDateToday = campaign.filterableLaunchDate
        ? campaign.filterableLaunchDate.isSame(dayjs(), 'day')
        : false;
      const marketingStatusConfig = getStatusConfig(campaign.marketingStatus);

      return {
        [CampaignTableColumnNames.Campaign]: (
          <AgxSpacer contentAlignment="start">
            <AgxAvatarLabelGroup
              id={`campaign-${index}-image`}
              label1={(addressLineOne ?? '') + (addressLineTwo ? `,` : '')}
              label2={addressLineTwo ?? ''}
              photoUrl={campaign.propertyImageUrls?.thumbnail ?? placeHolderImage}
              type="photo"
              variant="round"
              size="large"
            />
          </AgxSpacer>
        ),
        [CampaignTableColumnNames.Status]: (
          <AgxTypography
            id={`campaign-${index}-status`}
            size="small"
            type="body_ui"
            color="var(--text-secondary)"
          >
            {campaign.stage ? formatCampaignStageLabel(campaign.stage) : '--'}
          </AgxTypography>
        ),
        [CampaignTableColumnNames.LeadAgent]: campaign.leadAgentId ? (
          <AgxSpacer contentAlignment="start">
            <AgxAvatarLabelGroup
              id={`lead-agent-${index}`}
              label1={campaign.leadAgentName ?? 'Unassigned'}
              type="avatar"
              variant="round"
              size="small"
              photoUrl={leadAgent?.photoUrl ?? undefined}
              responsiveBreakpoint={Breakpoints.Laptop}
            />
          </AgxSpacer>
        ) : (
          '--'
        ),
        [CampaignTableColumnNames.SecondAgent]: campaign.secondAgentId ? (
          <AgxSpacer contentAlignment="start">
            <AgxAvatarLabelGroup
              id={`second-agent-${index}`}
              label1={campaign.secondAgentName ?? 'Unassigned'}
              type="avatar"
              variant="round"
              size="small"
              photoUrl={secondAgent?.photoUrl ?? undefined}
              responsiveBreakpoint={Breakpoints.Laptop}
            />
          </AgxSpacer>
        ) : (
          '--'
        ),
        [CampaignTableColumnNames.Marketing]:
          campaign.marketingStatus && marketingStatusConfig ? (
            <AgxBadge
              type="colourfill"
              colour={marketingStatusConfig.badgeColor}
              size="md"
              text={marketingStatusConfig.text}
            />
          ) : (
            '--'
          ),
        [CampaignTableColumnNames.Contract]: campaign.blankContractIsLive ? (
          <AgxBadge type="colourfill" colour="green" size="md" text="Contract Live" />
        ) : (
          <AgxBadge type="colourfill" colour="red" size="md" text="Contract Offline" />
        ),
        [CampaignTableColumnNames.Launch]: campaign.filterableLaunchDate ? (
          launchDateToday ? (
            <AgxBadge
              type="highlighted"
              variant="normal"
              size="md"
              highlightText="Today"
              text={`Launch ${campaign.filterableLaunchDate?.format('Do MMM')}`}
              colour="yellow"
            />
          ) : (
            <AgxBadge
              type="colourfill"
              variant="normal"
              size="md"
              text={`${isLaunched ? 'Launched' : 'Launch'} ${campaign.filterableLaunchDate?.format('Do MMM')}`}
              colour={isLaunched ? 'grey' : 'orange'}
            />
          )
        ) : (
          <AgxBadge
            type="colourfill"
            variant="normal"
            size="md"
            text="Launch not set"
            colour="grey"
          />
        ),
        [CampaignTableColumnNames.Action]: (
          <CampaignOptions
            key={campaign.id}
            selectedCampaign={campaign}
            onPerformCampaignAction={onPerformCampaignAction}
            isArchived={isArchivedTab}
            dataTestId={`agx-campaign-options-${index}`}
          />
        ),
      };
    });
  }, [agents, campaigns, isArchivedTab, onPerformCampaignAction]);

  return (
    <AgxTable
      columns={campaignsTableHeaders}
      rows={campaignColumns}
      variant={!isLoading && campaigns?.length ? 'default' : 'minimal'}
      showLoadingState={isLoading}
      tableStyle="square"
      noDataLabel={isLoading ? 'Loading...' : 'No campaigns found.'}
      noDataGlyphType="house"
      responsive
      virtualized={{
        rowHeight:
          screenSize === Breakpoints.Desktop || screenSize === Breakpoints.Laptop
            ? CAMPAIGN_DESKTOP_ROW_HEIGHT
            : CAMPAIGN_ROW_HEIGHT,
        overscanRows: CAMPAIGN_ROW_OVERSCAN,
      }}
      renderRowLink={(index, linkProps) => {
        const campaign = campaigns[index];
        if (!agencyId || !campaign.id) return;
        return (
          <Link
            {...linkProps}
            to={`/${Pages.AGENCIES}/${agencyId}/${Pages.CAMPAIGNS}/${campaign.id}/${Pages.CAMPAIGN_OVERVIEW}`}
            aria-label={
              (Array.isArray(campaign.cleanAddress)
                ? campaign.cleanAddress
                    .map((line) => line.trim())
                    .filter(Boolean)
                    .join(', ')
                : campaign.cleanAddress
              ).trim() || `Campaign ${campaign.id}`
            }
          />
        );
      }}
    />
  );
}
